import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  CalendarIcon,
  UsersIcon,
  EyeIcon,
  PencilIcon,
  FunnelIcon,
  Bars3BottomLeftIcon,
} from "@heroicons/react/24/outline";

import { apiService } from "../services/api";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { generateAccessCode } from "../utils/helpers";
import { useAuth } from "../contexts/AuthContext";
import { RoleGuard } from "../components/ui/RoleGuard";

const Evaluations = () => {
  const { user } = useAuth();
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [filteredEvaluations, setFilteredEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvaluationId, setEditingEvaluationId] = useState(null);
  const [evalLimitInfo, setEvalLimitInfo] = useState(null); // { current, limit }

  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "draft", label: "Draft" },
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
  ];

  const sortOptions = [
    { value: "date", label: "Date Created" },
    { value: "title", label: "Title" },
    { value: "status", label: "Status" },
  ];

  const [evaluationForm, setEvaluationForm] = useState({
    name: "",
    season: "",
    year: new Date().getFullYear(),
    description: "",
    start_date: "",
    end_date: "",
    assignment_mode: "open",
  });

  const loadEvaluations = useCallback(async () => {
    try {
      setLoading(true);
      const evaluationsData = await apiService.getEvaluations(clubId);
      setEvaluations(evaluationsData);
    } catch (error) {
      console.error("Failed to load evaluations:", error);
      toast.error("Failed to load evaluations");
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    loadEvaluations();
    apiService.getClubSubscriptionLimits(clubId)
      .then((limits) => setEvalLimitInfo({ current: limits.evaluations.current, limit: limits.evaluations.limit }))
      .catch((err) => console.warn("Could not load subscription limits:", err));
  }, [loadEvaluations, clubId]);

  useEffect(() => {
    let filtered = [...evaluations];

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter((evaluation) =>
        evaluation.title.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (evaluation) => evaluation.status === statusFilter,
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "title":
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        case "date":
        default:
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredEvaluations(filtered);
  }, [evaluations, searchTerm, statusFilter, sortBy, sortOrder]);

  const getStatusBadgeProps = (status) => {
    switch (status) {
      case "active":
        return { className: "bg-green-100 text-green-800" };
      case "completed":
        return { className: "bg-slate-100 text-slate-800" };
      case "draft":
        return { className: "bg-gray-100 text-gray-800" };
      default:
        return { className: "bg-gray-100 text-gray-800" };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const evalAtLimit = evalLimitInfo?.limit != null && evalLimitInfo.current >= evalLimitInfo.limit;
  const evalLimitTooltip = evalAtLimit
    ? `Your plan allows ${evalLimitInfo.limit} evaluation${evalLimitInfo.limit === 1 ? "" : "s"} per year. Upgrade your plan to create more.`
    : undefined;

  const handleCreateEvaluation = async () => {
    if (evalAtLimit) return;
    try {
      const newEval = {
        name: `Evaluation ${evaluations.length + 1}`,
        clubId,
        status: "draft",
        accessCode: generateAccessCode(),
        assignment_mode: "open",
      };

      await apiService.createEvaluation(newEval);
      await loadEvaluations();
      toast.success("Evaluation created successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to create evaluation");
    }
  };

  const handleViewEvaluation = (evaluationId) => {
    navigate(`/portal/club/${clubId}/evaluation/${evaluationId}`);
  };

  const handleUpdateEvaluation = async (e) => {
    e.preventDefault();
    if (!editingEvaluationId) {
      toast.error("No evaluation selected for editing");
      return;
    }

    try {
      const updatedEvaluation = {
        ...evaluationForm,
        clubId: clubId,
      };
      await apiService.updateEvaluation(editingEvaluationId, updatedEvaluation);
      loadEvaluations();
      setShowEditModal(false);
      setEditingEvaluationId(null);
      setEvaluationForm({
        name: "",
        season: "",
        year: new Date().getFullYear(),
        description: "",
        start_date: "",
        end_date: "",
      });
      toast.success("Evaluation updated successfully!");
    } catch (error) {
      toast.error("Failed to update evaluation");
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Evaluations</h1>
          <p className="text-gray-600">
            Manage and monitor all player evaluations
          </p>
        </div>
        {user &&
          (user.role === "club_admin" || user.role === "super_admin") && (
            <div className="relative group">
              <Button onClick={evalAtLimit ? undefined : handleCreateEvaluation} disabled={evalAtLimit}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Evaluation
              </Button>
              {evalAtLimit && (
                <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                  {evalLimitTooltip}
                </div>
              )}
            </div>
          )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search evaluations..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 appearance-none bg-white"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort By */}
            <div className="relative">
              <Bars3BottomLeftIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 appearance-none bg-white"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort Order */}
            <Button
              variant="outline"
              onClick={toggleSortOrder}
              className="flex items-center justify-center"
            >
              {sortOrder === "asc" ? "↑" : "↓"}{" "}
              {sortOrder === "asc" ? "Ascending" : "Descending"}
            </Button>
          </div>

          {/* Results Summary */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredEvaluations.length} of {evaluations.length}{" "}
            evaluations
          </div>
        </CardContent>
      </Card>

      {/* Evaluations List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredEvaluations.length === 0 ? (
          <div className="col-span-full">
            <Card>
              <CardContent className="p-12 text-center">
                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {evaluations.length === 0
                    ? "No evaluations yet"
                    : "No matching evaluations"}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {evaluations.length === 0
                    ? "Get started by creating your first evaluation"
                    : "Try adjusting your search criteria or filters"}
                </p>
                {evaluations.length === 0 && (
                  <div className="mt-6">
                    {user &&
                      (user.role === "club_admin" ||
                        user.role === "super_admin") && (
                        <div className="relative group inline-block">
                          <Button onClick={evalAtLimit ? undefined : handleCreateEvaluation} disabled={evalAtLimit}>
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Create Evaluation
                          </Button>
                          {evalAtLimit && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                              {evalLimitTooltip}
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          filteredEvaluations.map((evaluation) => (
            <Card
              key={evaluation.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleViewEvaluation(evaluation.id)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {evaluation.name}
                    </h3>
                    <p className="text-sm text-gray-900 mt-1">
                      {evaluation.season || "No season specified"}{" "}
                      {evaluation.year || "No year specified"}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {evaluation.description || "No description"}
                    </p>
                  </div>
                  <Badge {...getStatusBadgeProps(evaluation.status)}>
                    {evaluation.status.charAt(0).toUpperCase() +
                      evaluation.status.slice(1)}
                  </Badge>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Start {formatDate(evaluation.start_date)} - End{" "}
                    {formatDate(evaluation.end_date)}
                  </div>
                  <div className="flex items-center">
                    <UsersIcon className="h-4 w-4 mr-2" />
                    {evaluation.player_count || 0} players
                  </div>
                </div>

                <div className="flex justify-end space-x-2 mt-4 pt-4 border-t border-gray-200">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewEvaluation(evaluation.id);
                    }}
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  <RoleGuard roles={["super_admin", "club_admin"]}>
                    {evaluation.status !== "completed" && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingEvaluationId(evaluation.id);
                          setEvaluationForm({
                            name: evaluation.name,
                            season: evaluation.season,
                            year: evaluation.year,
                            description: evaluation.description,
                            start_date: evaluation.start_date || "",
                            end_date: evaluation.end_date || "",
                            assignment_mode:
                              evaluation.assignment_mode || "open",
                          });
                          setShowEditModal(true);
                        }}
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    )}
                  </RoleGuard>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      {/* Evaluation Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingEvaluationId(null);
        }}
        title="Edit Evaluation"
      >
        <form onSubmit={handleUpdateEvaluation} className="space-y-4">
          <Input
            label="Evaluation Name"
            value={evaluationForm.name}
            onChange={(e) =>
              setEvaluationForm({ ...evaluationForm, name: e.target.value })
            }
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Season"
              placeholder="Fall, Spring, Summer"
              value={evaluationForm.season}
              onChange={(e) =>
                setEvaluationForm({ ...evaluationForm, season: e.target.value })
              }
            />
            <Input
              label="Year"
              type="number"
              value={evaluationForm.year}
              onChange={(e) =>
                setEvaluationForm({
                  ...evaluationForm,
                  year: parseInt(e.target.value),
                })
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              value={evaluationForm.start_date}
              onChange={(e) =>
                setEvaluationForm({
                  ...evaluationForm,
                  start_date: e.target.value,
                })
              }
            />
            <Input
              label="End Date"
              type="date"
              value={evaluationForm.end_date}
              onChange={(e) =>
                setEvaluationForm({
                  ...evaluationForm,
                  end_date: e.target.value,
                })
              }
            />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <p className="text-sm text-slate-800">
              <strong>Start Date:</strong> First day of evaluations
              <br />
              <strong>End Date:</strong> Last day of evaluations
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
              rows="3"
              placeholder="Describe the purpose and goals of this evaluation..."
              value={evaluationForm.description}
              onChange={(e) =>
                setEvaluationForm({
                  ...evaluationForm,
                  description: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Check-in Workflow
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
              value={evaluationForm.assignment_mode}
              onChange={(e) =>
                setEvaluationForm({
                  ...evaluationForm,
                  assignment_mode: e.target.value,
                })
              }
            >
              <option value="open">
                Open Check-in (players can be checked in without pre-assignment)
              </option>
              <option value="assigned">
                Pre-assigned (players must be assigned to sessions first)
              </option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              This sets the default workflow for new sessions. You can change
              the workflow for individual sessions if needed.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Evaluations;
